import { Phone, Mail, MapPin, AlertTriangle } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import Eyebrow from "../components/Eyebrow";
import Breadcrumbs from "../components/Breadcrumbs";
import FamilyInquiryForm from "../components/forms/FamilyInquiryForm";
import { Reveal, KineticLines } from "../components/Reveal";
import { usePageMeta } from "../lib/seo";
import { SITE, EMERGENCY_NOTE } from "../lib/site";

export default function Contact() {
  const [params] = useSearchParams();
  const home = params.get("home") || "";
  usePageMeta({
    title: "Contact: Talk With Doris",
    description:
      "Tell CareCraft what's happening. Call (818) 590-1533 or send a message. Guidance is complimentary to families in Los Angeles.",
    path: "/contact",
  });

  return (
    <>
      <section className="bg-ivory pt-[72px]">
        <div className="mx-auto max-w-site px-4 pb-16 pt-10 sm:px-6 md:pb-20 md:pt-14 lg:px-12">
          <Breadcrumbs trail={[{ label: "Contact" }]} />
          <div className="mt-10 max-w-3xl">
            <Eyebrow>Talk with Doris</Eyebrow>
            <h1 className="mt-6 font-serif text-4xl font-medium leading-[1.15] tracking-tight text-forest sm:text-5xl lg:text-[3.6rem]">
              <KineticLines lines={[<>Start with</>, <>a conversation.</>]} />
            </h1>
            <Reveal delay={0.4}>
              <p className="mt-7 text-lg leading-[1.7] text-charcoal/80 md:text-xl">
                Tell us what's happening and we'll take it from there. Every conversation begins
                with listening.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-warmwhite pb-24">
        <div className="mx-auto grid max-w-site gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:px-12">
          <div className="lg:col-span-4">
            <Reveal>
              <div className="rounded-2xl border border-inkline bg-ivory p-8">
                <p className="text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-forest/60">
                  Contact details
                </p>
                <p className="mt-5 font-serif text-2xl font-medium text-forest">
                  {SITE.founder}
                </p>
                <p className="mt-1 text-[0.85rem] text-charcoal/55">
                  Founder, CareCraft
                </p>
                <ul className="mt-7 space-y-4 text-[1rem]">
                  <li>
                    <a
                      href={SITE.phoneHref}
                      data-testid="contact-phone"
                      className="inline-flex items-center gap-3 font-medium text-forest transition-colors hover:text-clay"
                    >
                      <Phone className="h-5 w-5 text-clay" aria-hidden="true" />
                      {SITE.phoneDisplay}
                    </a>
                  </li>
                  <li>
                    <a
                      href={`mailto:${SITE.email}`}
                      data-testid="contact-email"
                      className="inline-flex items-center gap-3 font-medium text-forest transition-colors hover:text-clay"
                    >
                      <Mail className="h-5 w-5 text-clay" aria-hidden="true" />
                      {SITE.email}
                    </a>
                  </li>
                  <li className="flex items-center gap-3 text-charcoal/80">
                    <MapPin className="h-5 w-5 text-clay" aria-hidden="true" />
                    {SITE.area}
                  </li>
                </ul>
                <p className="mt-8 border-t border-inkline pt-6 font-serif text-lg italic text-forest">
                  {SITE.tagline}
                </p>
              </div>
              <div className="mt-6 flex items-start gap-3 rounded-xl border border-clay/40 bg-clay-soft p-5">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-clay" aria-hidden="true" />
                <p className="text-[0.85rem] leading-relaxed text-charcoal/80">{EMERGENCY_NOTE}</p>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <Reveal delay={0.1}>
              <div className="rounded-2xl border border-inkline p-8 md:p-10">
                <h2 className="font-serif text-2xl font-medium text-forest md:text-3xl">
                  Tell us about your situation
                </h2>
                <p className="mt-3 text-charcoal/75">
                  There's no wrong way to start. Share only what you're comfortable sharing.
                </p>
                <div className="mt-8">
                  <FamilyInquiryForm home={home} />
                </div>
                <p className="mt-8 border-t border-inkline pt-6 text-[0.85rem] leading-relaxed text-charcoal/55">
                  Please don't include detailed medical histories in this form. We'll ask the right
                  questions together. CareCraft responds to calls and emails within 24 hours. If you
                  need urgent help, please call {SITE.phoneDisplay}.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
